// ============================================================================
// RESPONDER COMO ASESOR — LA lógica de enviar. UNA sola vez, para todo el panel.
// T-LAT-3 (R-LAT-02 · I-6).
//
// ⛔⛔ POR QUÉ ESTE ARCHIVO EXISTE, Y POR QUÉ NO VIVE DENTRO DE `datos.ts`.
//
// 1) POR QUÉ UNA SOLA COPIA. Lo de abajo costó una autopsia: decide si el BOT se
//    calla antes de que hable la persona. El inbox y el panel lateral del embudo
//    escriben en la misma conversación; con dos copias, la primera corrección
//    que llegue a una no llegaría a la otra y el síntoma —dos voces
//    contradiciéndose delante del cliente— se vería en una venta real, no en el
//    CI. Quien necesite enviar, llama a esta función. No se copia.
//
// 2) POR QUÉ UN MÓDULO APARTE Y NO `datos.ts`. Las pruebas del panel fingen
//    `datos.ts` POR MÓDULO (`vi.mock("…/datos.ts", …)`), y ese doblaje sustituye
//    los EXPORTS que ve quien importa el módulo, no las llamadas internas: una
//    función escrita dentro de `datos.ts` que llamara a `transferirAHumano`
//    usaría la LOCAL, nunca la fingida. La lógica quedaría fuera del alcance de
//    las varas del inbox sin que nadie se enterara. Aquí se importa desde fuera,
//    así que el doblaje sí manda.
// ============================================================================
import { enviarTexto, explicarError, reclamarChat, transferirAHumano } from "./datos.ts";
import type { Conversacion } from "./tipos.ts";

type SB = any;

/**
 * Manda el mensaje del asesor, dejando la conversación en manos de la persona.
 *
 * `alCambiar` recibe el PARCHE optimista que el anfitrión tiene que pintar en su
 * propio estado (la lista del inbox, la conversación del lateral): esta función
 * no sabe cómo guarda cada pantalla sus conversaciones.
 *
 * ⛔ NO recarga los mensajes: eso lo hace quien llama, que es quien sabe si el
 * asesor sigue mirando ese hilo.
 */
export async function responderComoAsesor(
  sb: SB,
  conv: Conversacion,
  texto: string,
  usuarioId: string | null,
  alCambiar: (parche: Partial<Conversacion>) => void,
): Promise<void> {
  // Si el bot está atendiendo y un humano escribe, el humano toma el chat:
  // dos voces en la misma conversación confunden al cliente.
  //
  // ⭐ Y CONTESTAR TAMBIÉN RECLAMA. Tras el arreglo del pase al humano, una
  // conversación escalada llega en `humano` SIN dueño: el asesor que responde
  // sin apretar el botón la dejaría en «Esperando asesor» para siempre, y a
  // los tres minutos otro asesor la ve libre y le escriben encima al cliente.
  //
  // ⚠️ Todo esto va en `try/catch` y NO bloquea el envío: un usuario staff de
  // Victorix sin fila activa en `miembros` pasa `transferir_a_humano` y
  // revienta en `asignar_conversacion` con `P0006`. Hoy ese camino funciona:
  // dejar que la asignación se tragara el mensaje del asesor sería regresión.
  // ⚠️ `usuarioId` es `string | null` de verdad: sin saber quién toma el chat
  // no se puede apuntar dueño (y mandar `null` BORRA el que hubiera).
  if (usuarioId) {
    // 🔴 EL PASE AL HUMANO VA FUERA DEL `try`, Y ES LA MITAD DEL ARREGLO.
    // Si `transferir_a_humano` falla (timeout, base saturada — la madrugada
    // del 10-ago, sin ir más lejos) la conversación se queda en `bot`: el
    // worker sigue creyendo que manda él y le contesta al cliente POR ENCIMA
    // del asesor. Dos voces contradiciéndose delante del cliente. Ese error
    // sube, el `Hilo` lo pinta en rojo y el mensaje NO sale como si nada.
    if (conv.estado === "bot") {
      await transferirAHumano(sb, conv.id);
      alCambiar({ estado: "humano" });
    }
    // ⚠️ Y SOLO LA ASIGNACIÓN va en `try/catch`, que es para lo único que el
    // plan lo autorizaba: un usuario staff de Victorix sin fila activa en
    // `miembros` pasa el pase y revienta aquí con `P0006`. Quedarse sin dueño
    // es un chip menos exacto; tragarse el mensaje del asesor es una venta.
    // El dueño optimista se pinta SOLO dentro del éxito: pintarlo antes
    // enseñaría «Asesor» sobre una conversación que en la base no lo tiene.
    if (conv.estado === "bot" || (conv.estado === "humano" && !conv.asignada_a)) {
      try {
        await reclamarChat(sb, conv.id, usuarioId);
        alCambiar({ asignada_a: usuarioId });
      } catch (e) {
        console.error("No se pudo apuntar el dueño de la conversación", e);
      }
    }
  }
  try {
    await enviarTexto(sb, conv.id, texto, usuarioId);
  } catch (e: any) {
    throw Object.assign(new Error(explicarError(e)), { mensaje: explicarError(e) });
  }
}
